import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Link2, Unlink, Wallet as WalletIcon } from "lucide-react";

const PROVIDERS = [
  { id: "metamask", name: "MetaMask", flag: "isMetaMask", color: "#F6851B", install: "https://metamask.io/download/" },
  { id: "trust", name: "Trust Wallet", flag: "isTrust", color: "#3375BB", install: "https://trustwallet.com/download" },
  { id: "coinbase", name: "Coinbase Wallet", flag: "isCoinbaseWallet", color: "#0052FF", install: "https://www.coinbase.com/wallet/downloads" },
  { id: "binance", name: "Binance Web3", flag: "isBinance", color: "#F0B90B", install: "https://www.binance.com/en/web3wallet" },
];

export const WalletConnectors = ({ connected, onChange }) => {
  const [busy, setBusy] = useState(null);

  const connect = async (p) => {
    const eth = window.ethereum;
    if (!eth) {
      toast.info(`${p.name} not detected — opening install page`);
      window.open(p.install, "_blank");
      return;
    }
    setBusy(p.id);
    try {
      const accounts = await eth.request({ method: "eth_requestAccounts" });
      const address = accounts?.[0];
      if (!address) throw new Error("No account returned");
      const { data } = await api.post("/wallet/connect", { address, provider: p.name });
      toast.success(`${p.name} connected — ${address.slice(0, 6)}…${address.slice(-4)}`);
      onChange?.(data);
    } catch (err) {
      toast.error(apiError(err, err?.message || "Connection rejected"));
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async () => {
    try {
      await api.post("/wallet/disconnect");
      toast.success("Wallet disconnected");
      onChange?.(null);
    } catch {
      toast.error("Disconnect failed");
    }
  };

  return (
    <div className="panel p-6 mb-8" data-testid="wallet-connectors">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="label-mono text-neon-green">// connect_your_wallet</div>
          <p className="text-white/50 text-sm font-body mt-1">Link your crypto wallet for faster deposits and withdrawals.</p>
        </div>
        {connected && (
          <div className="flex items-center gap-3" data-testid="connected-wallet-chip">
            <span className="flex items-center gap-2 px-3 py-1.5 border border-neon-green/50 text-neon-green font-mono text-xs">
              <Link2 className="w-3.5 h-3.5" />
              {connected.provider} · {connected.address.slice(0, 6)}…{connected.address.slice(-4)}
            </span>
            <button onClick={disconnect} className="btn-ghost !p-2" data-testid="disconnect-wallet-button" title="Disconnect">
              <Unlink className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {PROVIDERS.map((p) => (
          <button
            key={p.id}
            onClick={() => connect(p)}
            disabled={busy === p.id}
            className="flex items-center gap-3 p-3.5 border border-neon-line bg-black/30 hover:border-white/40 transition-colors text-left"
            data-testid={`connect-${p.id}`}
          >
            <span className="w-9 h-9 rounded-full grid place-items-center border-2 shrink-0" style={{ borderColor: p.color }}>
              <WalletIcon className="w-4 h-4" style={{ color: p.color }} />
            </span>
            <span>
              <span className="font-display font-bold text-sm block leading-tight">{p.name}</span>
              <span className="label-mono text-white/40">{busy === p.id ? "connecting…" : "connect"}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};
